import { Text } from "@mantine/core";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { CROSSBAR_DEFAULT_GO_HOME, CROSSBAR_DEFAULT_SHOOT } from "_constants";
import { useAddCrossbarParticipationMutation } from "crossbar/api/crossbar-participation.api";
import { Crossbar } from "crossbar/types/crossbar.model";
import { CrossbarParticipation } from "crossbar/types/crossbar-participation.model";
import Btn from "../atoms/Btn";
import { GoalkeeperSide } from "./GoalkeeperSelector";
import Result from "../atoms/Result";
import { DEFAULT_WHITE_COLOR } from "../../../_graphic_design";

export interface Props {
  crossbar: Crossbar;
  goalkeeperSide: GoalkeeperSide;
  onDone: () => void;
  participation: CrossbarParticipation;
}

const VIDEO_WIDTH = 346;
const OVERLAY_WIDTH = 100;
const RESULT_POSITION = { left: 20, right: 20, top: 300 };

// Using the same keys as Crossbar['goalkeeper_*']['videos'] to make the mapping easier
const OVERLAYS = [
  "down_right",
  "down_left",
  "upper_left",
  "upper_right",
] as const;
type Overlay = (typeof OVERLAYS)[number];
type OverlayConfig = {
  left?: number;
  right?: number;
  top?: number;
  border: string;
  borderRadius: string;
  content: string;
};
const OVERLAYS_MAPPING: Record<Overlay, OverlayConfig> = {
  down_right: {
    top: 160,
    right: 20,
    border: "10px solid #1E2B49",
    borderRadius: "20px",
    content: "PETIT FILET DROIT",
  },
  down_left: {
    left: 20,
    top: 160,
    border: "10px solid #1E2B49",
    borderRadius: "20px",
    content: "PETIT FILET GAUCHE",
  },
  upper_left: {
    left: 20,
    top: 60,
    border: "10px solid #1E2B49",
    borderRadius: "20px",
    content: "LUCARNE GAUCHE",
  },
  upper_right: {
    right: 20,
    top: 60,
    border: "10px solid #1E2B49",
    borderRadius: "20px",
    content: "LUCARNE DROITE",
  },
};

export default function Shoot({
  crossbar,
  participation,
  goalkeeperSide,
  onDone,
}: Props): ReactElement {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [addParticipation, addParticipationResult] =
    useAddCrossbarParticipationMutation();

  const [selectedOverlay, setSelectedOverlay] = useState<Overlay | null>(null);
  const [showOverlays, setShowOverlays] = useState<boolean>(false);
  const [showResult, setShowResult] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  const { videos } = crossbar[`goalkeeper_${goalkeeperSide}`];

  useEffect(() => {
    if (!addParticipationResult.data) {
      return;
    }
    const { status } = addParticipationResult.data;
    const videoElt = videoRef.current;
    if (!videoElt || !selectedOverlay) {
      return;
    }
    videoElt.src = videos[`${status}_${selectedOverlay}`];
    videoElt.play();
  }, [addParticipationResult, selectedOverlay, videos]);

  const status = addParticipationResult.data?.status;

  const onVideoEnded = () => {
    if (status) {
      // Result video => show the result
      setShowResult(true);
      onDone();
      return;
    }

    // Intro video ended => show the overlays
    setShowOverlays(true);
  };

  const onOverlayClick = (overlay: Overlay) => {
    setSelectedOverlay(overlay);
  };

  const onShootClick = async () => {
    const videoElt = videoRef.current;
    if (!videoElt || !selectedOverlay) {
      return;
    }

    setShowOverlays(false);
    addParticipation(participation);
  };

  return (
    <>
      <div style={{ position: "relative" }}>
        <video
          autoPlay={true}
          controls={false}
          disablePictureInPicture={true}
          onEnded={onVideoEnded}
          playsInline={true}
          ref={videoRef}
          src={videos.intro}
          style={{ minWidth: VIDEO_WIDTH, width: VIDEO_WIDTH }}
        />

        {showOverlays &&
          OVERLAYS.map((o) => (
            <div
              key={o}
              onClick={() => onOverlayClick(o)}
              style={{
                ...OVERLAYS_MAPPING[o],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  selectedOverlay === o ? "#1E2B49B0" : "#FFFFFFB0",
                boxSizing: "border-box",
                cursor: "pointer",
                height: OVERLAY_WIDTH,
                position: "absolute",
                width: OVERLAY_WIDTH,
                overflow: "hidden",
                color: selectedOverlay === o ? "#FFFFFF" : "#1E2B49",
                border: OVERLAYS_MAPPING[o].border,
                borderRadius: OVERLAYS_MAPPING[o].borderRadius,
              }}
            >
              {OVERLAYS_MAPPING[o].content}
            </div>
          ))}

        {showResult && status && (
          <div
            style={{
              ...RESULT_POSITION,
              background: DEFAULT_WHITE_COLOR,
              padding: 10,
              position: "absolute",
            }}
          >
            <Result crossbar={crossbar} status={status} />
          </div>
        )}
      </div>

      {showResult && status && (
        <Text
          className="text-xl text-center"
          sx={{
            color: DEFAULT_WHITE_COLOR,
          }}
          mb={5}
          mt={5}
          tt="uppercase"
          dangerouslySetInnerHTML={{
            __html:
              status === "goal"
                ? crossbar.messages.winner_explanation_msg
                : crossbar.messages.looser_explanation_msg,
          }}
        />
      )}

      {!showResult && (
        <Btn
          disabled={selectedOverlay === null}
          label={CROSSBAR_DEFAULT_SHOOT}
          onClick={onShootClick}
          visible={showOverlays}
        />
      )}

      <Btn
        label={CROSSBAR_DEFAULT_GO_HOME}
        onClick={() => navigate(`/event/${eventId}`, { replace: true })}
        visible={showResult}
      />
    </>
  );
}
